import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  linkedSignal,
  OnInit,
  signal } from '@angular/core';
import {
  backNavigation,
  queryParam,
  SortHeaderDirective,
  title } from '@softline/application';
import { IconComponent, modalFn} from '@softline/ui-core';
import { EditZeitraumFormDialogComponent } from '../../dialogs/edit-zeitraum-form-dialog/edit-zeitraum-form-dialog.component';
import { Router } from '@angular/router';
import { base64Decode, base64Encode, isObjectSort, Sort, Store } from '@softline/core';
import { Zeitraum } from '../../data/zeitraum';
import { CommonModule } from '@angular/common';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import { RohstoffbilanzResult } from '../../data/result';
import { RohstoffbilanzDetail } from '../../data/detail';
import { RohstoffbilanzDetailService } from '../../services/detail.service';
import { RohstoffbilanzService } from '../../services/rohstoffbilanz.service';

import { FormatPipe } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-produktion-rohstoffbilanz-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.html'],
  imports: [
    CommonModule,
    PageComponent,
    PageHeaderComponent,
    IconComponent,
    SortHeaderDirective,
    FormatPipe,
    TranslatePipe],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush })
export default class DashboardComponent implements OnInit {
  readonly rohstoffbilanzService = inject(RohstoffbilanzService);
  readonly rohstoffbilanzDetailService = inject(RohstoffbilanzDetailService);

  readonly modal = modalFn();

  readonly lagersymbole = this.rohstoffbilanzService.all;
  readonly details = this.rohstoffbilanzDetailService.all;

  readonly loadingDetails = signal(false);

  readonly sort = signal<Sort<RohstoffbilanzResult> | null>(null);

  readonly selectedRow = linkedSignal<
    { lagerymbole: any[]; selection?: any },
    RohstoffbilanzResult | null
  >({
    source: computed(() => ({
      lagerymbole: this.lagersymbole(),
      selection: this.selection() })),
    computation: (source) => {
      if (source?.lagerymbole && source?.lagerymbole?.length > 0 && source?.selection) {
        return (
          source.lagerymbole.find(
            (o) =>
              o.symbol.bezeichnung === source.selection.lagersymbol.symbol.bezeichnung
          ) ?? null
        );
      }

      return null;
    } });

  readonly sortedDetails = computed(() => {
    const details = this.details();
    const sort = this.sort();

    if (!sort || !isObjectSort(sort)) return details;

    const [key1, key2] = (sort.property as string).split('.');

    if (
      !['soll', 'ist', 'diff'].includes(key1) &&
      (key2 === 'artikel' || key2 === 'hfArtikel')
    ) {
      return details.sort((a, b) => {
        return sort.direction === 'desc'
          ? a[key1]?.[key2]?.localeCompare(b[key1]?.[key2])
          : b[key1]?.[key2]?.localeCompare(a[key1]?.[key2]);
      });
    }

    return details.sort((a, b) => {
      if (sort.direction === 'desc') {
        return b?.[key1]?.[key2] > a?.[key1]?.[key2] ? 1 : -1;
      } else {
        return a?.[key1]?.[key2] > b?.[key1]?.[key2] ? 1 : -1;
      }
    });
  });

  fromDate = queryParam('vondatum');
  toDate = queryParam('bisdatum');

  readonly routeQueryParams = computed(() => ({
    vondatum: this.fromDate(),
    bisdatum: this.toDate() }));

  readonly query = computed(() => {
    return {
      vondatum: this.fromDate(),
      bisdatum: this.toDate() };
  });

  readonly selection = computed(() => {
    const queryParams = this.routeQueryParams();

    if (queryParams?.['selection']) {
      return JSON.parse(base64Decode(queryParams?.['selection'] ?? '')) as {
        lagersymbol: RohstoffbilanzResult;
        row?: RohstoffbilanzDetail;
      };
    }

    return null;
  });

  readonly #detailQuery = computed(() => {
    const row = this.selectedRow();
    const query = this.query();
    if (!row?.symbol || (!query.bisdatum && !query.vondatum)) return undefined;

    return {
      vondatum: query?.vondatum,
      bisdatum: query?.bisdatum,
      symbol: row?.symbol };
  });

  constructor(
    private readonly router: Router,
    private readonly legacyStore: Store
  ) {
    title('Rohstoffbilanz');
    backNavigation('/');
    this.rohstoffbilanzService.register(this.query);
    this.rohstoffbilanzDetailService.register(this.#detailQuery);
  }

  async ngOnInit(): Promise<void> {
    if (this.query()?.vondatum && this.query()?.bisdatum) return;
    await this.editZeitraum();
  }

  async editZeitraum(): Promise<void> {
    const result:
      | { vondatum: string | null; bisdatum: string | null }
      | 'DISMISSED'
      | null = await this.modal({
      title: 'Zeitraum bearbeiten',
      component: EditZeitraumFormDialogComponent,
      dismiss: true,
      data: {
        vondatum: (this.query()?.vondatum ?? null) as any,
        bisdatum: (this.query()?.bisdatum ?? null) as any } });

    if (!result || result === 'DISMISSED' || !result?.vondatum || !result?.bisdatum)
      return;

    await this.updateZeitraum(result as any);
  }

  async navigateToProduktionsauftrag(row: RohstoffbilanzDetail): Promise<void> {
    if (!row?.idkopfpr) return;

    await this.router.navigate(['produktionsauftrag', row.idkopfpr], {
      queryParams: {
        idbeweauf: row.idkopfpr,
        origin: base64Encode(
          JSON.stringify({
            url: 'rohstoffbilanz',
            queryParams: base64Encode(
              JSON.stringify({
                ...this.query(),
                selection: {
                  lagersymbol: this.selectedRow(),
                  row } })
            ) })
        ) } });
  }

  sortChanged(event: Sort<RohstoffbilanzResult> | null): void {
    this.sort.set(event);
  }

  private async updateZeitraum(zeitraum: Zeitraum): Promise<void> {
    await this.router.navigate(['/rohstoffbilanz'], { queryParams: zeitraum });
  }
}
